import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Input } from "../components/ui/input";
import { Trophy, Gift, Sparkles, Mail, Loader2, ChevronRight, Pencil, Check, X } from "lucide-react";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const STATUS_LABEL = {
  ok: { text: "Email inviata", color: "text-forest" },
  mocked: { text: "Email simulata (MOCK)", color: "text-ink-500" },
  error: { text: "Email non inviata", color: "text-compass" },
  pending: { text: "Invio in corso", color: "text-ocean" },
};

export default function Profile() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingNick, setEditingNick] = useState(false);
  const [nickDraft, setNickDraft] = useState("");
  const [savingNick, setSavingNick] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.get("/me/profile")
      .then((r) => { if (mounted) setData(r.data); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-gold" size={36} />
      </div>
    );
  }
  if (!data) return null;

  const { level, tiers, unlocked_rewards: rewards, total_points } = data;
  const current = level.current;
  const next = level.next;
  // Helper: mostra l'immagine del badge se presente, altrimenti l'emoji (default 🏆)
  const renderTierIcon = (tier, sizeClass = "text-3xl") => {
    if (tier?.image_url) {
      return (
        <img
          src={tier.image_url}
          alt={tier.name || "Livello"}
          className="w-full h-full object-cover rounded-2xl"
        />
      );
    }
    return <span className={sizeClass} aria-hidden="true">{tier?.emoji || "🏆"}</span>;
  };
  const displayName = current?.name || "In cammino";

  const startEditNick = () => {
    setNickDraft(user?.nickname || "");
    setEditingNick(true);
  };
  const cancelEditNick = () => {
    setEditingNick(false);
    setNickDraft("");
  };
  const saveNick = async () => {
    const val = nickDraft.trim();
    if (val.length < 2) {
      toast.error("Il nickname deve avere almeno 2 caratteri");
      return;
    }
    setSavingNick(true);
    try {
      const { data: res } = await api.patch("/me/nickname", { nickname: val });
      toast.success("Nickname aggiornato");
      setEditingNick(false);
      // aggiorna localStorage + contesto auth
      const stored = JSON.parse(localStorage.getItem("odismap_user") || "{}");
      stored.nickname = res.nickname;
      localStorage.setItem("odismap_user", JSON.stringify(stored));
      if (refresh) await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore aggiornamento nickname");
    } finally {
      setSavingNick(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="profile-page">
      {/* Header utente */}
      <div className="flex items-center gap-3">
        <div className="w-14 h-14 rounded-2xl bg-gold flex items-center justify-center shadow-tactile overflow-hidden">
          {current ? renderTierIcon(current, "text-3xl") : <span className="text-3xl">🏆</span>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-widest text-ink-500 font-bold">Profilo · livello</div>
          <h1 className="font-display font-extrabold text-2xl text-ink-900 truncate">
            {user?.nickname || user?.name || "Esploratore"}
          </h1>
        </div>
      </div>

      {/* Card nickname pubblico */}
      <Card className="card-treasure p-4" data-testid="nickname-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-2xl bg-parchment-dark flex items-center justify-center shrink-0">
            <Sparkles size={18} className="text-gold-hover"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-widest text-ink-500 font-bold">Nickname pubblico</div>
            {!editingNick ? (
              <div className="flex items-center gap-2 mt-1">
                <div className="font-display font-extrabold text-xl text-ink-900 truncate" data-testid="nickname-value">
                  {user?.nickname || "—"}
                </div>
                <button
                  onClick={startEditNick}
                  className="ml-auto inline-flex items-center gap-1 text-xs font-bold text-ocean hover:underline"
                  data-testid="nickname-edit-btn"
                >
                  <Pencil size={14}/> Modifica
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 mt-1">
                <Input
                  autoFocus
                  value={nickDraft}
                  maxLength={24}
                  onChange={(e) => setNickDraft(e.target.value)}
                  className="h-10 rounded-xl border-outline"
                  data-testid="nickname-input"
                />
                <button
                  onClick={saveNick}
                  disabled={savingNick}
                  className="h-10 px-3 rounded-xl bg-forest text-white font-bold text-sm inline-flex items-center gap-1 disabled:opacity-50"
                  data-testid="nickname-save-btn"
                >
                  <Check size={16}/>
                </button>
                <button
                  onClick={cancelEditNick}
                  disabled={savingNick}
                  className="h-10 px-3 rounded-xl bg-parchment-dark text-ink-800 font-bold text-sm inline-flex items-center gap-1"
                  data-testid="nickname-cancel-btn"
                >
                  <X size={16}/>
                </button>
              </div>
            )}
            <p className="text-[11px] text-ink-500 mt-1">
              Mostrato agli altri esploratori in classifiche e notifiche. Il tuo nome vero resta privato.
            </p>
          </div>
        </div>
      </Card>

      {/* Card livello attuale */}
      <Card className="card-treasure p-5" data-testid="level-card">
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-2xl bg-gold-light border-2 border-gold/40 flex items-center justify-center overflow-hidden shrink-0">
            {current ? renderTierIcon(current, "text-5xl") : <span className="text-5xl">🏆</span>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-ink-500 font-bold">
              {current ? "Sei" : "Nessun livello ancora sbloccato"}
            </div>
            <div className="font-display font-extrabold text-xl text-ink-900">{displayName}</div>
            <div className="text-sm text-ink-700 mt-0.5">
              <Trophy size={14} className="inline mr-1 text-gold-hover"/>
              <span className="font-bold" data-testid="profile-total-points">{total_points} pt</span>
            </div>
          </div>
        </div>
        {next ? (
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs font-bold text-ink-700 mb-1">
              <span className="flex items-center gap-1">
                <span>Prossimo livello:</span>
                {next.image_url ? (
                  <img src={next.image_url} alt="" className="w-4 h-4 object-cover rounded-sm inline-block"/>
                ) : (
                  <span>{next.emoji}</span>
                )}
                <span>{next.name}</span>
              </span>
              <span>{level.points}/{next.min_points} pt</span>
            </div>
            <Progress value={level.progress_pct} className="h-3" data-testid="level-progress"/>
            <div className="text-[11px] text-ink-500 mt-1">
              Ti mancano <strong>{Math.max(next.min_points - level.points, 0)} punti</strong> per sbloccare {current ? "il prossimo livello" : "il primo livello"}.
            </div>
          </div>
        ) : current ? (
          <div className="mt-3 text-xs font-bold text-forest" data-testid="max-level">
            🏆 Sei al massimo livello — leggenda assoluta!
          </div>
        ) : (
          <div className="mt-3 text-xs text-ink-500 italic" data-testid="no-tiers">
            Nessun livello configurato: chiedi all'admin di crearne almeno uno.
          </div>
        )}
      </Card>

      {/* Tutti i tier */}
      <div>
        <h2 className="font-display font-bold text-lg text-ink-900 mb-2">Tutti i livelli</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {tiers.map((t) => {
            const reached = level.points >= t.min_points;
            const active = current && t.code === current.code;
            return (
              <div
                key={t.code}
                className={`p-3 rounded-2xl border-2 text-center ${
                  active ? "bg-gold-light border-gold" :
                  reached ? "bg-white border-outline" :
                  "bg-parchment-dark border-outline opacity-60"
                }`}
                data-testid={`tier-${t.code}`}
              >
                <div className={`w-14 h-14 mx-auto rounded-xl overflow-hidden flex items-center justify-center ${active ? "bg-white" : "bg-parchment-dark"}`}>
                  {renderTierIcon(t, "text-3xl")}
                </div>
                <div className={`text-xs font-bold mt-1 ${reached ? "text-ink-900" : "text-ink-500"}`}>{t.name}</div>
                <div className="text-[10px] text-ink-500">{t.min_points} pt</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Omaggi sbloccati */}
      <div>
        <h2 className="font-display font-bold text-lg text-ink-900 mb-2 flex items-center gap-2">
          <Gift size={18} className="text-compass"/> Omaggi sbloccati
        </h2>
        {rewards.length === 0 ? (
          <Card className="card-treasure p-6 text-center" data-testid="no-rewards">
            <Sparkles className="mx-auto text-ink-500 mb-2" size={32}/>
            <p className="text-ink-700 font-bold">Nessun omaggio ancora sbloccato</p>
            <p className="text-sm text-ink-500 mt-1">Completa i percorsi con un alto % di completamento per sbloccare premi.</p>
            <button
              onClick={() => navigate("/")}
              className="btn-tactile mt-4 inline-flex items-center gap-2"
              data-testid="profile-explore-btn"
            >
              Esplora percorsi <ChevronRight size={16}/>
            </button>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="rewards-list">
            {rewards.map((r) => {
              const st = STATUS_LABEL[r.email_status] || STATUS_LABEL.pending;
              return (
                <Card key={r.record_id} className="card-treasure p-4" data-testid={`reward-${r.record_id}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-gold flex items-center justify-center shrink-0">
                      <Gift size={20} className="text-ink-900"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-extrabold text-ink-900">{r.title}</div>
                      <div className="text-[11px] text-ink-500 truncate">
                        {r.route_title} · {r.completion_percent}% completato
                      </div>
                      {r.message && (
                        <div className="text-sm text-ink-700 mt-1.5 leading-snug">{r.message}</div>
                      )}
                      {r.gift_code && (
                        <div className="mt-2 inline-block px-3 py-1 bg-gold-light border-2 border-gold rounded-xl font-mono text-sm font-bold tracking-wider" data-testid={`reward-code-${r.record_id}`}>
                          {r.gift_code}
                        </div>
                      )}
                      <div className={`flex items-center gap-1 text-[11px] mt-2 ${st.color}`}>
                        <Mail size={12}/> {st.text}
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
